import React, { useState } from "react";
import {
  GraduationCap,
  CheckCircle2,
  FileText,
  PlayCircle,
  BadgeCheck,
  Clock,
  ExternalLink,
  Video,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";

const COMPLETION_FUNCTION = "sales_affiliate_training_completion";
const TRAINING_VIDEO_ACK_ITEM_CODE = "SA_ONBOARDING_TRAINING_VIDEO_ACK";
const LEGACY_ORIENTATION_VIDEO_ITEM_CODE = "SA_ONBOARDING_ORIENTATION_VIDEO";
const LEGACY_CODE_OF_CONDUCT_ITEM_CODE = "SA_ONBOARDING_CODE_OF_CONDUCT_ACK";

// Approved final Sales Affiliate Training Video (YouTube Unlisted).
const TRAINING_VIDEO_YOUTUBE_ID = "DEG3bEe1VAs";
const TRAINING_VIDEO_EMBED_URL = TRAINING_VIDEO_YOUTUBE_ID
  ? `https://www.youtube-nocookie.com/embed/${TRAINING_VIDEO_YOUTUBE_ID}?rel=0`
  : "";

const TYPE_ICON = {
  VIDEO: PlayCircle,
  DOCUMENT: FileText,
  ACKNOWLEDGEMENT: BadgeCheck,
};

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return null;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min`;
}

function StatusPill({ item }) {
  if (item.complete) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-emerald-700">
        <CheckCircle2 size={14} /> Complete
      </span>
    );
  }

  if (item.status === "IN_PROGRESS") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-blue-700">
        In progress
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-slate-600">
      Not complete
    </span>
  );
}

function RequiredPill({ isRequired }) {
  return isRequired ? (
    <span className="rounded-full bg-slate-900 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-[0.14em] text-white">
      Required
    </span>
  ) : (
    <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-[0.14em] text-slate-600">
      Optional
    </span>
  );
}

export default function SalesAffiliateTrainingSection({
  requirement,
  refreshWorkspace,
}) {
  const [busyItem, setBusyItem] = useState("");
  const [itemError, setItemError] = useState({});
  const [openedDocs, setOpenedDocs] = useState(() => new Set());
  const [videoAcknowledged, setVideoAcknowledged] = useState(false);

  if (!requirement) return null;

  const inventory = Array.isArray(requirement.inventory)
    ? requirement.inventory
    : [];
  const requiredTotal = Number(requirement.required_total || 0);
  const requiredCompleted = Number(requirement.required_completed || 0);
  const progressPercent = Number(requirement.progress_percent || 0);

  const videoAcknowledgementItem = inventory.find(
    (item) => item.item_code === TRAINING_VIDEO_ACK_ITEM_CODE
  );

  // The legacy orientation-video item remains preserved in the training data model
  // for history/provider-neutral future use. The launch UX uses the dedicated
  // self-attested training-video acknowledgement instead, so do not render the
  // unavailable legacy provider-completion card alongside the embedded video.
  const visibleInventory = inventory.filter(
    (item) =>
      item.item_code !== TRAINING_VIDEO_ACK_ITEM_CODE &&
      item.item_code !== LEGACY_ORIENTATION_VIDEO_ITEM_CODE &&
      !(
        item.item_code === LEGACY_CODE_OF_CONDUCT_ITEM_CODE &&
        item.is_required !== true
      )
  );

  const markOpened = (itemCode) => {
    setOpenedDocs((prev) => {
      const next = new Set(prev);
      next.add(itemCode);
      return next;
    });
  };

  const handleComplete = async (item, completionKind) => {
    if (busyItem) return false;
    setItemError((prev) => ({ ...prev, [item.item_code]: "" }));
    setBusyItem(item.item_code);

    try {
      const { data, error } = await supabase.functions.invoke(
        COMPLETION_FUNCTION,
        {
          body: {
            item_code: item.item_code,
            completion_kind: completionKind,
            acknowledged: true,
          },
        }
      );

      if (error) {
        throw new Error(error.message || "Unable to record completion.");
      }
      if (!data?.ok) {
        throw new Error(data?.error || "Unable to record completion.");
      }

      await refreshWorkspace();
      return true;
    } catch (err) {
      setItemError((prev) => ({
        ...prev,
        [item.item_code]: err?.message || "Unable to record completion.",
      }));
      return false;
    } finally {
      setBusyItem("");
    }
  };

  const handleTrainingVideoCompletion = async () => {
    if (
      !videoAcknowledgementItem ||
      !TRAINING_VIDEO_EMBED_URL ||
      !videoAcknowledged ||
      videoAcknowledgementItem.complete
    ) {
      return;
    }

    const saved = await handleComplete(
      videoAcknowledgementItem,
      "ACKNOWLEDGEMENT"
    );
    if (saved) {
      setVideoAcknowledged(false);
    }
  };

  const renderItemAction = (item, busy) => {
    // action_state from the server is the authoritative source of what the
    // affiliate may do with this item.
    switch (item.action_state) {
      case "ACKNOWLEDGE":
        return (
          <button
            type="button"
            disabled={busy}
            onClick={() => handleComplete(item, "ACKNOWLEDGEMENT")}
            className="rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-black text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? "Saving..." : "I acknowledge this item"}
          </button>
        );
      case "REVIEW_DOCUMENT": {
        const opened = openedDocs.has(item.item_code);
        return (
          <div className="flex flex-col items-start gap-2 sm:items-end">
            {item.content_ref ? (
              <a
                href={item.content_ref}
                target="_blank"
                rel="noreferrer"
                onClick={() => markOpened(item.item_code)}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-black text-slate-800 shadow-sm hover:bg-slate-50"
              >
                Open document <ExternalLink size={15} />
              </a>
            ) : null}
            <button
              type="button"
              disabled={busy || !opened}
              onClick={() => handleComplete(item, "DOCUMENT_REVIEW")}
              title={opened ? undefined : "Open the document first."}
              className="rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-black text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? "Saving..." : "I have reviewed this item"}
            </button>
            {!opened ? (
              <span className="text-[11px] font-semibold text-slate-500">
                Open the document to enable review.
              </span>
            ) : null}
          </div>
        );
      }
      case "AWAITING_CONTENT":
        return (
          <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-slate-600">
            Not yet available
          </span>
        );
      case "AWAITING_VIDEO_PROVIDER":
        return (
          <span className="max-w-[15rem] text-right text-xs font-semibold leading-5 text-slate-500">
            Video completion is verified by the video provider and is not yet
            available in this workspace.
          </span>
        );
      case "COMPLETE":
      case "NO_ACTION":
      default:
        return null;
    }
  };

  const trainingVideoBusy =
    busyItem === videoAcknowledgementItem?.item_code;
  const trainingVideoError = videoAcknowledgementItem
    ? itemError[videoAcknowledgementItem.item_code]
    : "";
  const trainingVideoComplete = Boolean(videoAcknowledgementItem?.complete);
  const trainingVideoCanComplete =
    Boolean(TRAINING_VIDEO_EMBED_URL) &&
    Boolean(videoAcknowledgementItem) &&
    videoAcknowledgementItem?.action_state === "ACKNOWLEDGE" &&
    !trainingVideoComplete;

  return (
    <section
      id="training"
      className="mt-8 rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <GraduationCap className="text-blue-600" size={24} />
          <h2 className="text-xl font-black text-slate-950">Assigned Training</h2>
        </div>
        {requiredTotal > 0 ? (
          <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-slate-600">
            {requiredCompleted} / {requiredTotal} required · {progressPercent}%
          </div>
        ) : null}
      </div>

      <p className="mt-2 text-sm leading-6 text-slate-600">
        {requiredTotal > 0
          ? "Complete each required training item below. Optional items are recommended but do not block activation."
          : "No training is required for activation. Optional items, when available, are recommended."}
      </p>

      <div className="mt-5 overflow-hidden rounded-3xl border border-blue-200 bg-white shadow-sm">
        <div className="border-b border-blue-100 bg-blue-50 px-5 py-5">
          <div className="flex items-start gap-3">
            <Video className="mt-0.5 flex-none text-blue-600" size={25} />
            <div>
              <div className="text-xs font-black uppercase tracking-[0.2em] text-blue-600">
                Required training
              </div>
              <h3 className="mt-1 text-xl font-black tracking-tight text-slate-950">
                Sales Affiliate Training Video
              </h3>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Watch the entire training video before confirming completion. It
                explains how to represent AI Fine Protector accurately, the limits
                of the Sales Affiliate role, approved prospect practices, and the
                commission framework.
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 py-5">
          {TRAINING_VIDEO_EMBED_URL ? (
            <div
              className="mx-auto w-full overflow-hidden rounded-2xl bg-slate-950 shadow-sm"
              style={{ maxWidth: "820px" }}
            >
              <iframe
                src={TRAINING_VIDEO_EMBED_URL}
                title="AI Fine Protector Sales Affiliate Training Video"
                className="block aspect-video w-full bg-black"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="mx-auto max-w-3xl rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center">
              <Video className="mx-auto text-slate-400" size={32} />
              <div className="mt-3 text-sm font-black text-slate-800">
                Training video is being finalized
              </div>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                The approved video will appear here before this training requirement
                is enabled for completion.
              </p>
            </div>
          )}

          {trainingVideoComplete ? (
            <div className="mt-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
              <CheckCircle2 className="mt-0.5 flex-none text-emerald-600" size={22} />
              <div>
                <div className="text-sm font-black text-emerald-900">
                  Training complete
                </div>
                <p className="mt-1 text-sm leading-6 text-emerald-800">
                  You confirmed that you watched the entire Sales Affiliate Training
                  Video, understand the role and restrictions explained in it, and
                  agree to follow approved AFP practices.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-5 border-t border-slate-100 pt-5">
              <label
                className={`flex items-start gap-3 text-sm font-semibold ${
                  trainingVideoCanComplete
                    ? "cursor-pointer text-slate-700"
                    : "cursor-not-allowed text-slate-400"
                }`}
              >
                <input
                  type="checkbox"
                  checked={videoAcknowledged}
                  onChange={(event) => setVideoAcknowledged(event.target.checked)}
                  disabled={!trainingVideoCanComplete || trainingVideoBusy}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600"
                />
                <span>
                  I confirm that I watched the entire AI Fine Protector™ Sales
                  Affiliate Training Video, understand the Sales Affiliate role and
                  requirements explained in the training, and agree to follow the
                  approved Sales Affiliate practices and restrictions.
                </span>
              </label>

              <button
                type="button"
                onClick={handleTrainingVideoCompletion}
                disabled={
                  !trainingVideoCanComplete ||
                  !videoAcknowledged ||
                  trainingVideoBusy
                }
                className="mt-4 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {trainingVideoBusy ? "Saving..." : "Complete Training"}
              </button>

              {!TRAINING_VIDEO_EMBED_URL ? (
                <p className="mt-2 text-xs font-semibold text-slate-500">
                  Completion will be enabled when the approved training video is published.
                </p>
              ) : null}
            </div>
          )}

          {trainingVideoError ? (
            <div
              role="alert"
              className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700"
            >
              {trainingVideoError}
            </div>
          ) : null}
        </div>
      </div>

      {visibleInventory.length === 0 ? null : (
        <div className="mt-5 space-y-4">
          {visibleInventory.map((item) => {
            const Icon = TYPE_ICON[item.item_type] || FileText;
            const duration = formatDuration(item.estimated_duration_seconds);
            const busy = busyItem === item.item_code;
            const error = itemError[item.item_code];

            return (
              <div
                key={`${item.item_code}:${item.item_version}`}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <Icon className="mt-0.5 flex-none text-blue-600" size={22} />
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-black text-slate-900">
                          {item.title}
                        </span>
                        <RequiredPill isRequired={item.is_required === true} />
                      </div>
                      {item.description ? (
                        <div className="mt-1 text-sm leading-6 text-slate-600">
                          {item.description}
                        </div>
                      ) : null}
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs font-bold text-slate-500">
                        <span className="uppercase tracking-[0.14em]">
                          {item.item_type}
                        </span>
                        {duration ? (
                          <span className="inline-flex items-center gap-1">
                            <Clock size={13} /> {duration}
                          </span>
                        ) : null}
                        {item.item_type === "VIDEO" ? (
                          <span>{item.progress_percent || 0}% viewed</span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-none flex-col items-start gap-3 sm:items-end">
                    <StatusPill item={item} />
                    {!item.complete ? renderItemAction(item, busy) : null}
                  </div>
                </div>

                {error ? (
                  <div
                    role="alert"
                    className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700"
                  >
                    {error}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
